/**
 * Currency Utilities
 */

export type CurrencyCode = 'KES' | 'USD' | 'EUR' | 'GBP' | 'JPY';

export interface CurrencyConfig {
  code: CurrencyCode;
  symbol: string;
  name: string;
  position: 'before' | 'after'; // Symbol position relative to amount
}

export const CURRENCIES: Record<CurrencyCode, CurrencyConfig> = {
  KES: { code: 'KES', symbol: 'KSh', name: 'Kenya Shillings', position: 'before' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', position: 'before' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', position: 'before' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', position: 'before' },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', position: 'before' },
};

/**
 * Format amount with currency symbol
 * @param amount - The numeric amount to format
 * @param currencyCode - Currency code (KES, USD, EUR, GBP, JPY)
 * @param includeDecimals - Whether to show decimal places (default: true)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number | string,
  currencyCode: CurrencyCode = 'KES',
  includeDecimals: boolean = true
): string {
  const currency = CURRENCIES[currencyCode] || CURRENCIES.KES;
  
  // Convert to number if it's a string
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle invalid numbers
  if (isNaN(numericAmount)) {
    return `${currency.symbol}0${includeDecimals ? '.00' : ''}`;
  }
  
  const formattedAmount = numericAmount.toLocaleString('en-US', {
    minimumFractionDigits: includeDecimals ? 2 : 0,
    maximumFractionDigits: includeDecimals ? 2 : 0,
  });

  if (currency.position === 'after') {
    return `${formattedAmount}${currency.symbol}`;
  } else {
    return `${currency.symbol}${formattedAmount}`;
  }
}

export function formatCurrencyWithSpacing(
  amount: number | string,
  currencyCode: CurrencyCode = 'KES',
  includeDecimals: boolean = true
): string {
  const currency = CURRENCIES[currencyCode] || CURRENCIES.KES;

  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) {
    return currency.position === 'after'
      ? `0${includeDecimals ? '.00' : ''} ${currency.symbol}`
      : `${currency.symbol} 0${includeDecimals ? '.00' : ''}`;
  }

  const formattedAmount = numericAmount.toLocaleString('en-US', {
    minimumFractionDigits: includeDecimals ? 2 : 0,
    maximumFractionDigits: includeDecimals ? 2 : 0,
  });

  if (currency.position === 'after') {
    return `${formattedAmount} ${currency.symbol}`;
  }

  return `${currency.symbol} ${formattedAmount}`;
}

/**
 * Get currency symbol
 * @param currencyCode - Currency code
 * @returns Currency symbol
 */
export function getCurrencySymbol(currencyCode: CurrencyCode = 'KES'): string {
  return CURRENCIES[currencyCode]?.symbol || CURRENCIES.KES.symbol;
}

/**
 * Get currency config
 * @param currencyCode - Currency code
 * @returns Currency configuration
 */
export function getCurrencyConfig(currencyCode: CurrencyCode = 'KES'): CurrencyConfig {
  return CURRENCIES[currencyCode] || CURRENCIES.KES;
}
